import { useAdminStats } from '../../../hooks/useAdminStats';

const StatCard = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <span className="text-[13px] text-white/50">{label}</span>
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${color}`}>
        <i className={`${icon} text-[18px] text-white`} />
      </div>
    </div>
    <div>
      <p className="text-[26px] font-700 text-white leading-tight">{value}</p>
      {sub && <p className="text-[12px] text-white/40 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const statusPayColor: Record<string, string> = {
  paid: 'bg-emerald-500/20 text-emerald-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  failed: 'bg-red-500/20 text-red-400',
};
const statusSubColor: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400',
  trial: 'bg-cyan-500/20 text-cyan-400',
  expired: 'bg-orange-500/20 text-orange-400',
  cancelled: 'bg-red-500/20 text-red-400',
  defaulter: 'bg-red-700/20 text-red-500',
};
const statusLabel: Record<string, string> = {
  paid: 'Pago',
  pending: 'Pendente',
  failed: 'Falhou',
  active: 'Ativa',
  trial: 'Trial',
  expired: 'Vencida',
  cancelled: 'Cancelada',
  defaulter: 'Inadimplente',
};

const AdminDashboardPage = () => {
  const { stats, loading } = useAdminStats();

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  const fmtShortDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
  };

  const maxCount = stats.dailyNewUsers.length > 0
    ? Math.max(...stats.dailyNewUsers.map((d) => d.count), 1)
    : 1;

  const hasAnyUsers = stats.dailyNewUsers.some((d) => d.count > 0);

  const chartLabels = (() => {
    const days = stats.dailyNewUsers;
    if (days.length < 4) return [];
    const idxs = [0, 9, 19, days.length - 1];
    return idxs.map((i) => fmtShortDate(days[i].date));
  })();

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[24px] font-700 text-white">Dashboard do SaaS</h1>
        <p className="text-white/40 text-[14px] mt-1">Visão geral da plataforma em tempo real</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Alerts */}
          {stats.alerts.length > 0 && (
            <div className="flex flex-col gap-2 mb-6">
              {stats.alerts.map((a, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-[13px] font-500 ${
                    a.type === 'error'
                      ? 'bg-red-500/10 border-red-500/30 text-red-400'
                      : a.type === 'warning'
                      ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                      : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
                  }`}
                >
                  <i
                    className={`text-[16px] shrink-0 ${
                      a.type === 'error'
                        ? 'ri-error-warning-line'
                        : a.type === 'warning'
                        ? 'ri-alert-line'
                        : 'ri-information-line'
                    }`}
                  />
                  {a.message}
                </div>
              ))}
            </div>
          )}

          {/* Métricas — linha 1: usuários */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <StatCard icon="ri-team-line" label="Total de Clientes" value={stats.totalUsers} sub="cadastrados" color="bg-violet-600" />
            <StatCard icon="ri-shield-star-line" label="Administradores" value={stats.totalAdmins} sub="usuários internos" color="bg-indigo-600" />
            <StatCard icon="ri-user-follow-line" label="Usuários Ativos" value={stats.activeUsers} sub={`de ${stats.totalUsers} total`} color="bg-emerald-600" />
            <StatCard icon="ri-user-add-line" label="Novos (30 dias)" value={stats.newLast30Days} sub="novos cadastros" color="bg-cyan-600" />
            <StatCard icon="ri-login-box-line" label="Logins Hoje" value={stats.loginsToday} sub="acessos registrados" color="bg-sky-600" />
          </div>

          {/* Métricas — linha 2: planos e receita */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon="ri-vip-crown-line" label="Plano Pago" value={stats.paidUsers} sub="assinantes pagos" color="bg-amber-500" />
            <StatCard icon="ri-user-line" label="Plano Free" value={stats.freeUsers} sub="usuários gratuitos" color="bg-white/20" />
            <StatCard icon="ri-flask-line" label="Em Trial" value={stats.trialUsers} sub="avaliação gratuita" color="bg-teal-600" />
            <StatCard icon="ri-close-circle-line" label="Inadimplentes" value={stats.defaulters} sub="assinaturas" color="bg-red-600" />
          </div>

          {/* Receita */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-[12px] text-white/40 mb-1">MRR Estimado</p>
              <p className="text-[28px] font-700 text-emerald-400">{fmt(stats.mrr)}</p>
              <p className="text-[12px] text-white/30 mt-1">receita recorrente mensal</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-[12px] text-white/40 mb-1">Receita do Mês</p>
              <p className="text-[28px] font-700 text-white">{fmt(stats.monthRevenue)}</p>
              <p className="text-[12px] text-white/30 mt-1">pagamentos recebidos</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col justify-between">
              <p className="text-[12px] text-white/40 mb-3">Status de Assinaturas</p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between text-[12px] mb-1.5">
                    <span className="text-white/50">Cancelamentos</span>
                    <span className="text-white font-600">{stats.cancelledSubscriptions}</span>
                  </div>
                  <div className="flex justify-between text-[12px] mb-1.5">
                    <span className="text-white/50">Pgtos Pendentes</span>
                    <span className="text-yellow-400 font-600">{stats.pendingPayments}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de crescimento */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <h2 className="text-[16px] font-600 text-white">Crescimento de Usuários</h2>
                <p className="text-white/40 text-[13px] mt-0.5">Novos cadastros nos últimos 30 dias</p>
              </div>
              <span className="text-[12px] font-600 px-3 py-1.5 bg-violet-600/20 text-violet-400 rounded-xl">
                +{stats.newLast30Days} este mês
              </span>
            </div>

            {!hasAnyUsers ? (
              <div className="flex flex-col items-center justify-center h-[120px] text-white/30 gap-2">
                <i className="ri-bar-chart-2-line text-3xl" />
                <p className="text-[13px]">Nenhum cadastro nos últimos 30 dias</p>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-[3px] h-[100px]">
                  {stats.dailyNewUsers.map((day) => {
                    const heightPct = Math.max((day.count / maxCount) * 100, day.count > 0 ? 8 : 2);
                    return (
                      <div
                        key={day.date}
                        className="flex-1 rounded-t transition-colors cursor-pointer group relative"
                        style={{
                          height: `${heightPct}%`,
                          backgroundColor: day.count > 0 ? 'rgba(124,58,237,0.5)' : 'rgba(255,255,255,0.06)',
                        }}
                      >
                        <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] text-white/70 bg-black/60 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                          {fmtShortDate(day.date)}: {day.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-[11px] text-white/30">
                  {chartLabels.map((label, i) => (
                    <span key={i}>{label}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Últimos pagamentos + assinaturas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Últimos pagamentos */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/10">
                <h3 className="text-[15px] font-600 text-white">Últimos Pagamentos</h3>
              </div>
              {stats.recentPayments.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-[13px]">Nenhum pagamento registrado</div>
              ) : (
                <div className="divide-y divide-white/5">
                  {stats.recentPayments.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                        <i className="ri-bank-card-line text-white/40 text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-white font-500 truncate">{p.user_name}</p>
                        <p className="text-[11px] text-white/40">{p.gateway} · {fmtDate(p.paid_at)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[13px] font-700 text-white">{fmt(p.amount)}</p>
                        <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${statusPayColor[p.status] || 'bg-white/10 text-white/40'}`}>
                          {statusLabel[p.status] || p.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Últimas assinaturas */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/10">
                <h3 className="text-[15px] font-600 text-white">Últimas Assinaturas</h3>
              </div>
              {stats.recentSubscriptions.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-[13px]">Nenhuma assinatura registrada</div>
              ) : (
                <div className="divide-y divide-white/5">
                  {stats.recentSubscriptions.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-8 h-8 rounded-full bg-violet-600/20 flex items-center justify-center shrink-0">
                        <i className="ri-file-list-3-line text-violet-400 text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-white font-500 truncate">{s.user_name}</p>
                        <p className="text-[11px] text-white/40">{s.plan_name} · {fmtDate(s.started_at)}</p>
                      </div>
                      <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full shrink-0 ${statusSubColor[s.status] || 'bg-white/10 text-white/40'}`}>
                        {statusLabel[s.status] || s.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;
